/**
 * PDF 내보내기 (작업지시서 P3-11)
 *
 * ── 설계 결정: 한글 텍스트를 어떻게 넣을 것인가 ────────────────────────
 *
 * PDF 의 기본 내장 폰트(Helvetica 등)에는 한글 글리프가 없다.
 * 한글을 **벡터 텍스트**로 넣으려면 TrueType 폰트를 서브셋해서 임베드해야 하는데,
 * 그건 폰트 파서를 직접 구현하는 일이라 이 단계의 범위를 넘는다.
 *
 * 선택지를 비교했다:
 *   A. 한글 텍스트 생략        → 마트 도면에서 구역명이 사라진다. 실무상 불가.
 *   B. 폰트 서브셋 임베드       → 정확하지만 구현 비용이 크다. 후속 과제.
 *   C. 도면 전체를 래스터로 embed → 한글 정확, 축척 정확, 구현 단순. 텍스트 선택 불가.
 *
 * **C 를 택했다.** 이유:
 *   · 이 PDF 의 용도는 "현장에 들고 가는 인쇄물"이다. 텍스트 선택은 필요 없다.
 *   · DoD 는 "인쇄 시 축척 1:100 실측 정확" — 이미지를 정확한 물리 크기로
 *     배치하면 그대로 만족한다.
 *   · JPEG 는 PDF 가 /DCTDecode 로 **그대로 담을 수 있어** 압축 구현이 불필요하다.
 *
 * 제목란·워터마크는 래스터 위에 그려 넣는다(같은 캔버스). 따라서 한글이 정상 출력된다.
 * 벡터 PDF 가 필요해지면 B 를 구현해 이 파일만 교체하면 된다.
 */

export type PaperSize = 'A4' | 'A3' | 'A2' | 'A1' | 'A0';
export type Orientation = 'portrait' | 'landscape';

/** 용지 크기 (mm, 세로 기준) */
export const PAPER_MM: Record<PaperSize, { w: number; h: number }> = {
  A4: { w: 210, h: 297 },
  A3: { w: 297, h: 420 },
  A2: { w: 420, h: 594 },
  A1: { w: 594, h: 841 },
  A0: { w: 841, h: 1189 },
};

const MM_PER_PT = 25.4 / 72; // PDF 좌표 단위는 pt (1/72 inch)

export function mmToPt(mm: number): number {
  return mm / MM_PER_PT;
}

export interface PdfPageSpec {
  paper: PaperSize;
  orientation: Orientation;
  /** 인쇄 DPI. 300 이면 현장 인쇄에 충분하다. */
  dpi: number;
}

export function pageSizeMm(spec: { paper: PaperSize; orientation: Orientation }): {
  w: number;
  h: number;
} {
  const p = PAPER_MM[spec.paper];
  return spec.orientation === 'landscape' ? { w: p.h, h: p.w } : { w: p.w, h: p.h };
}

/** 용지에 필요한 픽셀 수 (DPI 기준) */
export function pagePixels(spec: PdfPageSpec): { w: number; h: number } {
  const mm = pageSizeMm(spec);
  const px = (v: number) => Math.round((v / 25.4) * spec.dpi);
  return { w: px(mm.w), h: px(mm.h) };
}

/**
 * 도면을 지정 축척으로 용지에 담을 때 필요한 배율.
 *
 * @param scaleDenom 축척 분모. 100 이면 1:100.
 * @returns 도면 µm → 용지 mm 변환 계수
 */
export function drawingToPaperMm(scaleDenom: number): number {
  // 1:100 이면 실제 100mm 가 종이에서 1mm.
  // 내부 좌표는 µm 이므로 µm → mm 는 ÷1000, 거기에 다시 ÷축척분모.
  return 1 / (1000 * scaleDenom);
}

/**
 * 도면 크기(µm)를 용지에 맞추는 축척 분모를 고른다.
 * 실무에서 쓰는 표준 축척만 후보로 둔다 — 1:137 같은 값은 아무도 안 쓴다.
 */
export const STANDARD_SCALES = [
  1, 2, 5, 10, 20, 25, 50, 100, 150, 200, 250, 300, 500, 750, 1000, 1500, 2000,
];

export function fitScale(
  drawingWidthUm: number,
  drawingHeightUm: number,
  spec: { paper: PaperSize; orientation: Orientation },
  marginMm = 15
): number {
  const page = pageSizeMm(spec);
  const availW = Math.max(1, page.w - marginMm * 2);
  const availH = Math.max(1, page.h - marginMm * 2);

  for (const s of STANDARD_SCALES) {
    const f = drawingToPaperMm(s);
    if (drawingWidthUm * f <= availW && drawingHeightUm * f <= availH) return s;
  }
  return STANDARD_SCALES[STANDARD_SCALES.length - 1]!;
}

/* -------------------------------------------------------------------------- *
 * 최소 PDF 작성기
 * -------------------------------------------------------------------------- */

export interface PdfImagePage {
  /** JPEG 바이트 (그대로 /DCTDecode 로 들어간다) */
  jpeg: Uint8Array;
  widthPx: number;
  heightPx: number;
  paper: PaperSize;
  orientation: Orientation;
}

export interface PdfMeta {
  title?: string;
  author?: string;
  subject?: string;
}

/**
 * JPEG 한 장을 용지 전체에 채운 PDF 를 만든다.
 *
 * 구조를 최소로 유지한다 — 객체 5개 + xref.
 * 외부 라이브러리를 쓰지 않는 이유: PDF 라이브러리는 대부분 수백 KB 이고,
 * 우리가 필요한 것은 "이미지 한 장을 정확한 물리 크기로 배치" 뿐이다.
 */
export function buildImagePdf(page: PdfImagePage, meta: PdfMeta = {}): Uint8Array {
  const size = pageSizeMm(page);
  const wPt = mmToPt(size.w);
  const hPt = mmToPt(size.h);

  const enc = new TextEncoder();
  const chunks: Uint8Array[] = [];
  const offsets: number[] = [];
  let length = 0;

  const push = (data: Uint8Array | string) => {
    const bytes = typeof data === 'string' ? enc.encode(data) : data;
    chunks.push(bytes);
    length += bytes.byteLength;
  };
  const startObject = () => offsets.push(length);

  push('%PDF-1.4\n');
  // 바이너리를 포함한다는 표시 — 일부 뷰어가 이걸 보고 전송 모드를 정한다
  push(new Uint8Array([0x25, 0xe2, 0xe3, 0xcf, 0xd3, 0x0a]));

  // 1: Catalog
  startObject();
  push('1 0 obj\n<< /Type /Catalog /Pages 2 0 R >>\nendobj\n');

  // 2: Pages
  startObject();
  push('2 0 obj\n<< /Type /Pages /Kids [3 0 R] /Count 1 >>\nendobj\n');

  // 3: Page
  startObject();
  push(
    `3 0 obj\n<< /Type /Page /Parent 2 0 R ` +
      `/MediaBox [0 0 ${fmtPt(wPt)} ${fmtPt(hPt)}] ` +
      `/Resources << /XObject << /Im0 5 0 R >> >> ` +
      `/Contents 4 0 R >>\nendobj\n`
  );

  // 4: Content stream — 이미지를 용지 전체에 배치
  const content = `q\n${fmtPt(wPt)} 0 0 ${fmtPt(hPt)} 0 0 cm\n/Im0 Do\nQ\n`;
  startObject();
  push(`4 0 obj\n<< /Length ${enc.encode(content).byteLength} >>\nstream\n`);
  push(content);
  push('endstream\nendobj\n');

  // 5: Image XObject (JPEG 원본을 그대로)
  startObject();
  push(
    `5 0 obj\n<< /Type /XObject /Subtype /Image ` +
      `/Width ${page.widthPx} /Height ${page.heightPx} ` +
      `/ColorSpace /DeviceRGB /BitsPerComponent 8 ` +
      `/Filter /DCTDecode /Length ${page.jpeg.byteLength} >>\nstream\n`
  );
  push(page.jpeg);
  push('\nendstream\nendobj\n');

  // 6: Info
  startObject();
  push(
    `6 0 obj\n<< /Title (${pdfString(meta.title ?? 'StoreCAD 도면')}) ` +
      `/Author (${pdfString(meta.author ?? 'StoreCAD')}) ` +
      `/Subject (${pdfString(meta.subject ?? '')}) ` +
      `/Producer (StoreCAD Layout Studio) >>\nendobj\n`
  );

  // xref
  const xrefStart = length;
  const count = offsets.length + 1;
  let xref = `xref\n0 ${count}\n0000000000 65535 f \n`;
  for (const off of offsets) {
    xref += `${String(off).padStart(10, '0')} 00000 n \n`;
  }
  push(xref);
  push(`trailer\n<< /Size ${count} /Root 1 0 R /Info 6 0 R >>\nstartxref\n${xrefStart}\n%%EOF\n`);

  // 합치기
  const out = new Uint8Array(length);
  let at = 0;
  for (const c of chunks) {
    out.set(c, at);
    at += c.byteLength;
  }
  return out;
}

function fmtPt(v: number): string {
  return (Math.round(v * 1000) / 1000).toString();
}

/**
 * PDF 문자열 리터럴 이스케이프.
 * 비 ASCII 는 내장 폰트로 표현할 수 없으므로 메타데이터에서만 쓰고,
 * 표시용 텍스트는 래스터에 그린다.
 */
function pdfString(s: string): string {
  return s.replace(/[\\()]/g, (m) => `\\${m}`).replace(/[\r\n]/g, ' ');
}
